/**
 * ユーザー情報統合 Context
 * プロフィールとサブスクリプション情報を統合して管理
 *
 * GET /api/user/me から統合データを取得し、
 * 旧 ProfileContext と SubscriptionContext の機能を統合
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import {
  getUserMe,
  type UserMe,
  UserNotFoundError,
  UserUnauthorizedError,
  DEFAULT_FREE_SUBSCRIPTION,
  type PlanType,
  type UsageSummary,
  DEFAULT_PLAN_LIMITS,
} from '@/services/user';
import { PLAN_TYPE } from '@/constants/planTypes';
import { saveUserProfile, type UserProfileData } from '@/services/profile';
import { deleteAvatar, uploadAvatar } from '@/services/avatar';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useAuth } from '@/hooks/useAuth';
import { POLICY_ACTION, evaluatePolicies, type PolicyDocStatus } from '@/utils/policyStatus';
import type { components } from '@/types/api-schema';

import { UserContext } from './userContextObj';

type UserConsentSummary = components['schemas']['UserConsentSummary'];
type ConsentVersionStatus = components['schemas']['ConsentVersionStatus'];

export interface UserContextType {
  // ユーザー情報（プロフィール + サブスクリプション）
  user: UserMe | null;
  isLoading: boolean;
  error: string | null;
  // ユーザーが見つからない場合（HTTP 404）
  isUserNotFound: boolean;
  // 認証エラー（HTTP 401/403）
  isUserUnauthorized: boolean;
  // デフォルトの無料プランを使用中かどうか（エラー時のフォールバック）
  isUsingDefaultPlan: boolean;
  refreshUser: () => Promise<void>;
  updateProfile: (data: Partial<UserProfileData>) => Promise<void>;

  // === Profile-related properties ===
  displayName: string | null;

  // === Avatar ===
  /** 表示に使うアイコンURL（カスタム → Google連携のアイコン の順。無ければ null） */
  avatarUrl: string | null;
  /** 自分で設定したアイコンがあるか（「元に戻す」の出し分け用） */
  hasCustomAvatar: boolean;
  /** アイコンを設定する（トリミング済み画像の data URI を渡す） */
  setAvatar: (imageDataUri: string) => Promise<void>;
  /** カスタムアイコンを解除する（Google連携ならGoogleのアイコンに戻る） */
  clearAvatar: () => Promise<void>;
  // Consent granularity (for onboarding routing)
  /** 同意が要る文書が無い（改定の「確認」だけが残っていても true。utils/policyStatus.ts） */
  isConsentSatisfied: boolean;
  /** 文書ごとに求めること（同意・確認・なし）と、前回からの改定 */
  policyStatuses: PolicyDocStatus[];
  /** 今の版にすべて同意・確認済み（false のあいだ、AI アシスタントを使えない） */
  policiesCurrent: boolean;
  // Profile field granularity (for onboarding routing)
  isDisplayNameSatisfied: boolean;
  // Derived onboarding needs
  needsConsent: boolean;
  needsProfile: boolean;
  // プロフィールがサービス上で「完了」と見なせるかどうか
  isProfileComplete: boolean;

  // === Subscription-related properties ===
  subscription: UsageSummary | null;
  isSubscriptionAvailable: boolean;
  planType: PlanType;
  canCreateEvent: () => boolean;
  canGenerateAIPlan: () => boolean;
  canUseGuests: () => boolean;
  canUseOgShare: () => boolean;
  canUsePageDesign: () => boolean;
  canUseLineNotification: () => boolean;
  canUseAiUrlImport: () => boolean;
  getRemainingEvents: () => number;
  getRemainingAIGenerations: () => number;
  getEventLimit: () => number;
  getAIGenerationLimit: () => number;
  getMaxParticipantsLimit: () => number;
  getAiUrlImportLimit: () => number;
}

interface UserProviderProps {
  children: ReactNode;
}

/**
 * ユーザープロバイダー
 * アプリケーション全体でユーザー情報を共有
 */
export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const { user: authUser, isAuthenticated } = useAuth();
  const [user, setUser] = useState<UserMe | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUserNotFound, setIsUserNotFound] = useState(false);
  const [isUserUnauthorized, setIsUserUnauthorized] = useState(false);
  const [isUsingDefaultPlan, setIsUsingDefaultPlan] = useState(false);

  /**
   * ユーザー情報を取得して state に反映する。isLoading には触らない。
   *
   * isLoading を立てると ProtectedRoute が Outlet を LoadingSpinner に
   * 差し替えてページごとアンマウントしてしまう。初回ロードでは spinner を
   * 見せたいが、ユーザー操作中の refresh では背後で更新したいだけなので、
   * 取得ロジックと isLoading の ON/OFF を分離している。
   */
  const fetchAndApplyUser = useCallback(async () => {
    if (!isAuthenticated || !authUser) {
      setUser(null);
      setIsUserNotFound(false);
      setIsUserUnauthorized(false);
      setIsUsingDefaultPlan(false);
      return;
    }

    try {
      setError(null);
      setIsUserNotFound(false);
      setIsUserUnauthorized(false);
      setIsUsingDefaultPlan(false);

      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      if (!token) {
        throw new Error('認証トークンが取得できませんでした');
      }

      const data = await getUserMe(token);
      setUser(data);
    } catch (err) {
      console.error('Failed to load user:', err);
      const message = err instanceof Error ? err.message : 'ユーザー情報の取得に失敗しました';
      setError(message);

      if (err instanceof UserNotFoundError || (err instanceof Error && err.name === 'UserNotFoundError')) {
        setIsUserNotFound(true);
      }
      if (err instanceof UserUnauthorizedError || (err instanceof Error && err.name === 'UserUnauthorizedError')) {
        setIsUserUnauthorized(true);
      }

      // エラー時はデフォルトの無料プランサブスクリプションを設定
      // プロフィール部分はnullのまま
      setUser(null);
      setIsUsingDefaultPlan(true);
    }
  }, [isAuthenticated, authUser]);

  /**
   * プロフィールを更新
   */
  const updateProfile = useCallback(
    async (data: Partial<UserProfileData>) => {
      if (!isAuthenticated) {
        throw new Error('認証されていません');
      }

      try {
        setError(null);

        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();

        if (!token) {
          throw new Error('認証トークンが取得できませんでした');
        }

        await saveUserProfile(token, data);

        // ローカル状態を即座に更新（UI反応を高速化）
        setUser((prev) => (prev ? { ...prev, ...data } : null));
      } catch (err) {
        console.error('Failed to update profile:', err);
        setError(err instanceof Error ? err.message : 'プロフィールの更新に失敗しました');
        throw err;
      }
    },
    [isAuthenticated]
  );

  /**
   * アイコンを設定する。保存後のURLをそのままローカル状態へ反映する
   * （URLにはハッシュが入るので、CDNのキャッシュが残っていても新しい画像になる）
   */
  const setAvatar = useCallback(
    async (imageDataUri: string) => {
      if (!isAuthenticated) {
        throw new Error('認証されていません');
      }

      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      if (!token) {
        throw new Error('認証トークンが取得できませんでした');
      }

      const url = await uploadAvatar(token, imageDataUri);
      setUser((prev) => (prev ? { ...prev, avatarUrl: url } : prev));
    },
    [isAuthenticated]
  );

  /**
   * カスタムアイコンを解除する。
   * Cognito の picture 属性（Google連携時のアイコン）は触らないので、
   * 解除するとGoogleのアイコン表示に自動で戻る。
   */
  const clearAvatar = useCallback(async () => {
    if (!isAuthenticated) {
      throw new Error('認証されていません');
    }

    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    if (!token) {
      throw new Error('認証トークンが取得できませんでした');
    }

    await deleteAvatar(token);
    setUser((prev) => (prev ? { ...prev, avatarUrl: null } : prev));
  }, [isAuthenticated]);

  /**
   * 初回ロード時にユーザー情報を取得（isLoading を立ててスピナー表示）。
   * 以降の refresh は fetchAndApplyUser を直接呼ぶため isLoading は立たない。
   */
  useEffect(() => {
    setIsLoading(true);
    fetchAndApplyUser().finally(() => setIsLoading(false));
  }, [fetchAndApplyUser]);

  // === Profile-related computed values ===
  const displayName = user?.displayName || authUser?.displayName || null;

  // アイコンは「自分で設定したもの」が最優先。
  // 無ければ Cognito の picture（Google連携時にGoogleから入る値）を使う。
  const customAvatarUrl = user?.avatarUrl || null;
  const avatarUrl = customAvatarUrl || authUser?.avatar || null;

  const consents = user?.consents as UserConsentSummary[] | undefined;
  const hasTermsConsent = !!consents?.some((c) => c.consentType === 'terms' && c.granted);
  const hasPrivacyConsent = !!consents?.some((c) => c.consentType === 'privacy' && c.granted);

  const consentVersionStatus = user?.consentVersionStatus as ConsentVersionStatus | undefined;
  // 改定の中身（constants/policyRevisions.ts）を見て、同意が要るか・確認でよいかを決める
  const policyStatuses = useMemo(
    () => (consentVersionStatus ? evaluatePolicies(consents, consentVersionStatus) : []),
    [consents, consentVersionStatus],
  );
  const isConsentUpToDate = consentVersionStatus
    ? policyStatuses.every((status) => status.action !== POLICY_ACTION.AGREE)
    : hasTermsConsent && hasPrivacyConsent;
  const policiesCurrent = consentVersionStatus
    ? policyStatuses.every((status) => status.action === POLICY_ACTION.NONE)
    : isConsentUpToDate;

  const isDisplayNameSatisfied = !!user?.displayName;
  const isConsentSatisfied = isConsentUpToDate;
  const needsConsent = !!user && !isConsentSatisfied;
  const needsProfile = !!user && !isDisplayNameSatisfied;
  const isProfileComplete = !!user && isDisplayNameSatisfied && isConsentSatisfied;

  // === Subscription-related computed values ===
  const subscription: UsageSummary | null = user?.subscription || (isUsingDefaultPlan ? DEFAULT_FREE_SUBSCRIPTION : null);
  const isSubscriptionAvailable = !isLoading && subscription !== null;
  const planType: PlanType = subscription?.planType || PLAN_TYPE.FREE;

  const canCreateEvent = useCallback(() => {
    if (isLoading) return false;
    if (!subscription) return false;
    const remaining = subscription.remaining.activeEvents;
    return remaining > 0;
  }, [subscription, isLoading]);

  const canGenerateAIPlan = useCallback(() => {
    if (isLoading) return false;
    if (!subscription) return false;
    const remaining = subscription.remaining.aiGenerationsPerMonth;
    return remaining === -1 || remaining > 0;
  }, [subscription, isLoading]);

  const canUseGuests = useCallback(() => {
    if (!subscription) return true;
    return subscription.limits.allowGuests;
  }, [subscription]);

  const canUseOgShare = useCallback(() => {
    if (!subscription) return true;
    return subscription.limits.allowOgShare;
  }, [subscription]);

  const canUsePageDesign = useCallback(() => {
    if (!subscription) return false;
    return subscription.limits.allowPageDesign;
  }, [subscription]);

  const canUseLineNotification = useCallback(() => {
    if (!subscription) return false;
    return subscription.limits.allowLineNotification;
  }, [subscription]);

  const canUseAiUrlImport = useCallback(() => {
    if (!subscription) return false;
    return subscription.limits.allowAiUrlImport;
  }, [subscription]);

  const getRemainingEvents = useCallback(() => {
    if (!subscription) return 0;
    return subscription.remaining.activeEvents;
  }, [subscription]);

  const getRemainingAIGenerations = useCallback(() => {
    if (!subscription) return 0;
    return subscription.remaining.aiGenerationsPerMonth;
  }, [subscription]);

  const getEventLimit = useCallback(() => {
    if (!subscription) return DEFAULT_PLAN_LIMITS.free.maxActiveEvents;
    return subscription.limits.maxActiveEvents;
  }, [subscription]);

  const getAIGenerationLimit = useCallback(() => {
    if (!subscription) return DEFAULT_PLAN_LIMITS.free.aiGenerationsPerMonth;
    return subscription.limits.aiGenerationsPerMonth;
  }, [subscription]);

  const getMaxParticipantsLimit = useCallback(() => {
    if (!subscription) return DEFAULT_PLAN_LIMITS.free.maxParticipantsPerEvent;
    return subscription.limits.maxParticipantsPerEvent;
  }, [subscription]);

  const getAiUrlImportLimit = useCallback(() => {
    if (!subscription) return DEFAULT_PLAN_LIMITS.free.aiUrlImportsPerDay;
    return subscription.limits.aiUrlImportsPerDay;
  }, [subscription]);

  const value: UserContextType = {
    user,
    isLoading,
    error,
    isUserNotFound,
    isUserUnauthorized,
    isUsingDefaultPlan,
    refreshUser: fetchAndApplyUser,
    updateProfile,
    // Profile-related
    displayName,
    // Avatar
    avatarUrl,
    hasCustomAvatar: !!customAvatarUrl,
    setAvatar,
    clearAvatar,
    isConsentSatisfied,
    policyStatuses,
    policiesCurrent,
    isDisplayNameSatisfied,
    needsConsent,
    needsProfile,
    isProfileComplete,
    // Subscription-related
    subscription,
    isSubscriptionAvailable,
    planType,
    canCreateEvent,
    canGenerateAIPlan,
    canUseGuests,
    canUseOgShare,
    canUsePageDesign,
    canUseLineNotification,
    canUseAiUrlImport,
    getRemainingEvents,
    getRemainingAIGenerations,
    getEventLimit,
    getAIGenerationLimit,
    getMaxParticipantsLimit,
    getAiUrlImportLimit,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
